import { useState, useEffect } from 'react';
import { FiUser, FiPhone, FiMapPin, FiMail, FiLock } from 'react-icons/fi';
import api from '../services/api';
import ChangePasswordForm from '../components/auth/ChangePasswordForm';

interface ProfileData {
  family_name: string;
  country: string;
  state: string;
  phone_number: string;
  email: string;
}

export default function Profile() {
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await api.get('/profile');
        setProfile(response.data.data);
      } catch (err) {
        setError('Failed to load profile information');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfile();
  }, []);

  const handlePasswordChangeSuccess = () => {
    setShowChangePassword(false);
    setSuccessMessage('Password changed successfully');
    setTimeout(() => setSuccessMessage(''), 5000);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
            <div className="space-y-3">
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <div className="text-red-600 dark:text-red-400">{error}</div>
        </div>
      </div>
    );
  }

  if (!profile) return null;

  return (
    <div className="space-y-6">
      {successMessage && (
        <div className="bg-green-50 dark:bg-green-900/30 p-4 rounded-lg">
          <p className="text-green-700 dark:text-green-400 text-sm text-center">
            {successMessage}
          </p>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            Profile Information
          </h3>
        </div>
        
        <div className="px-6 py-5 space-y-6">
          <div className="flex items-center gap-x-3">
            <FiUser className="h-5 w-5 text-gray-400 dark:text-gray-500" />
            <div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Name</div>
              <div className="text-sm font-medium text-gray-900 dark:text-white">
                {profile.family_name}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-x-3">
            <FiMail className="h-5 w-5 text-gray-400 dark:text-gray-500" />
            <div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Email</div>
              <div className="text-sm font-medium text-gray-900 dark:text-white">
                {profile.email}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-x-3">
            <FiPhone className="h-5 w-5 text-gray-400 dark:text-gray-500" />
            <div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Phone</div>
              <div className="text-sm font-medium text-gray-900 dark:text-white">
                {profile.phone_number}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-x-3">
            <FiMapPin className="h-5 w-5 text-gray-400 dark:text-gray-500" />
            <div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Location</div>
              <div className="text-sm font-medium text-gray-900 dark:text-white">
                {profile.state}, {profile.country}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            Security
          </h3>
        </div>
        
        <div className="px-6 py-5">
          {showChangePassword ? (
            <ChangePasswordForm
              email={profile.email}
              onSuccess={handlePasswordChangeSuccess}
              onCancel={() => setShowChangePassword(false)}
            />
          ) : (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-x-3">
                <FiLock className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                <div>
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    Password
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    Change your password
                  </div>
                </div>
              </div>
              <button
                onClick={() => setShowChangePassword(true)}
                className="text-sm font-medium text-primary hover:text-primary-dark dark:text-primary-400 dark:hover:text-primary-300"
              >
                Change
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}